import { useEffect } from 'react';
import { mapSSEDataToGanttQueues } from '../utils';
import { useGroupedQueuesStore, visibleQueuesSelector } from '..';
import { useGanttStore } from '../Gantt/store';
import { GraphneEventSource } from '../eventSource/GraphneEventSource';
import { useProductionPlanSettings } from '../components/SettingsDrawer';
import { DateRange, GanttFilter, useGanttFilterStore } from './useGanttFilterStore';
import { getInstanceId } from '../Gantt/utils';

let ganttEventSource: GraphneEventSource | null = null;

export const useGanttProductionPlan = () => {
  const instanceId = getInstanceId();
  const hasVisibleQueues = useGroupedQueuesStore((state) => !!visibleQueuesSelector(state).length);
  const visibleQueues = useGroupedQueuesStore(visibleQueuesSelector);
  const setGanttQueues = useGanttStore((state) => state.setGanttQueues);
  const withDependenciesDetails = useProductionPlanSettings((state) => state.gantt.showDependencies);
  const setIsGanttLoading = useGanttStore((state) => state.setIsGanttLoading);
  const dateRange = useGanttFilterStore((state) => state.ganttDateRange);
  const ganttFilters = useGanttFilterStore((state) => state.ganttFilters);

  const closeEventSource = () => {
    if (ganttEventSource) {
      ganttEventSource.close();
    }
  };

  useEffect(() => {
    if (hasVisibleQueues) {
      const setupSSEConnection = (range: DateRange) => {
        if (ganttEventSource) {
          ganttEventSource.close();
        }

        const queueIds = visibleQueues
          .map((queue) => queue.id?.toString())
          .filter((id): id is string => id !== undefined);

        ganttEventSource = new GraphneEventSource({
          source: 'gantt',
          from: range.from,
          to: range.to,
          withDependenciesDetails,
          priority: ganttFilters.includes(GanttFilter.PRIORITY),
          requiresAcceptance: ganttFilters.includes(GanttFilter.TO_ACCEPT),
          onlyQueuesWithTasks: !ganttFilters.includes(GanttFilter.SHOW_WITHOUT_TASKS),
          isBehindSchedule: ganttFilters.includes(GanttFilter.DELAYED),
          queues: queueIds,
          onUpdate: (data) => {
            const queues = mapSSEDataToGanttQueues(data, { startDate: range.from, endDate: range.to });
            setGanttQueues(instanceId, queues);
            setIsGanttLoading(instanceId, false);
          },
        });
      };

      setupSSEConnection(dateRange);

      return () => {
        closeEventSource();
      };
    }

    closeEventSource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVisibleQueues, setGanttQueues, withDependenciesDetails, visibleQueues.length, dateRange, ganttFilters]);

  useEffect(
    () => () => {
      closeEventSource();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setGanttQueues],
  );

  return {};
};
